/**
 * bg-removal.js
 * Ashe Design – Background Removal: account + pricing page.
 *
 * 1. Loads the customer's credits (GET /account)
 * 2. Sends buy buttons to Stripe Checkout (POST /checkout/session)
 * 3. Sends "Manage billing" to Stripe's billing page (POST /checkout/portal)
 * 4. Shows a message when the customer comes back from Stripe
 *
 * Works with the other page scripts through events on [data-bgr-root]:
 *   sends        "bgr:account"          (latest account data)
 *   listens for  "bgr:credits-changed"  (the removal tool spent a credit)
 *   listens for  "bgr:pricing-change"   (a pricing dropdown changed)
 *
 * Safety: server text is only ever added with textContent (never innerHTML).
 */
(() => {
  'use strict';

  // ---------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------
  const USAGE_LIMIT = 10;
  // After a purchase, re-check for new credits after these waits (ms).
  const POLL_DELAYS_MS = [2000, 3000, 5000, 8000, 12000];
  const PAYMENT_PROBLEM_STATUSES = ['past_due', 'unpaid'];
  const SURFACE_LABELS = { plugin: 'Photoshop plugin', storefront: 'Website tool' };
  const TOTAL_BEFORE_CHECKOUT_KEY = 'bgr:totalBeforeCheckout';

  const ERROR_MESSAGES = {
    network: "We couldn't reach the server. Check your internet connection and try again.",
    not_authorized: 'Your login has expired. Please log in again.',
    trial_already_used: "You've already used your free trial. You can still buy credits below.",
    already_subscribed: 'You already have a plan. Use "Manage billing" to switch plans.',
    no_billing_account: "You haven't bought anything yet, so there's no billing to manage.",
    invalid_price: "That option isn't available right now. Please try another one or contact us.",
    invalid_kind: 'Something went wrong with that button. Please refresh the page and try again.',
    invalid_limit: 'Something went wrong loading your account. Please refresh the page.',
    revoked: "This account can't use background removal. Please contact us for help.",
    no_credits: "You're out of credits. Pick an option below to get more.",
    bad_redirect: 'Something went wrong opening checkout. Please try again.',
    unknown: 'Something went wrong. Please try again in a moment.',
  };

  const SUCCESS_MESSAGES = {
    trial: 'Your card is saved (you were not charged). Your free credits are on the way!',
    one_time: 'Thanks for your 1-Time Purchase! Your credits are on the way.',
    subscription: 'Thanks for subscribing! Your credits are on the way.',
  };
  const DEFAULT_SUCCESS_MESSAGE = 'Thanks! Your credits are on the way.';
  const CANCELLED_MESSAGE = 'Checkout was cancelled. You were not charged.';

  const NO_CREDITS_WITH_TRIAL = "You don't have any credits yet. Start your free trial above, or pick an option below.";
  const NO_CREDITS = "You don't have any credits right now. Pick an option below to get more.";

  // ---------------------------------------------------------------
  // Small helpers
  // ---------------------------------------------------------------
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  function formatCount(n) {
    return (Number(n) || 0).toLocaleString();
  }

  function plural(n, word) {
    return `${formatCount(n)} ${word}${Number(n) === 1 ? '' : 's'}`;
  }

  function formatDate(iso) {
    if (!iso) return '';
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const options = { month: 'long', day: 'numeric' };
    if (date.getFullYear() !== new Date().getFullYear()) options.year = 'numeric';
    return new Intl.DateTimeFormat(undefined, options).format(date);
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  }

  function readConfig(root) {
    const node = root.querySelector('[data-bgr-config]');
    if (!node) throw new Error('Config block is missing');
    const raw = JSON.parse(node.textContent);
    return {
      apiBase: String(raw.apiBase || '').replace(/\/+$/, ''),
      key: String(raw.key || ''),
      email: String(raw.email || ''),
            loginUrl: String(raw.loginUrl || '/account/login'),
      // Photo Studio credits page only: come back to Photo Studio after Stripe.
      returnTo: raw.returnTo === 'studio' ? 'studio' : '',
    };
  }

  // A button whose dropdown option has no Stripe price id yet.
  const isUnavailable = (button) => button.hasAttribute('data-bgr-unavailable');

  // ---------------------------------------------------------------
  // Text for the account panel (pure functions, easy to test)
  // ---------------------------------------------------------------
  function breakdownText(balance) {
    const parts = [];
    if (balance.expiring > 0) parts.push(`${formatCount(balance.expiring)} expire`);
    if (balance.neverExpire > 0) parts.push(`${formatCount(balance.neverExpire)} never expire`);
    return parts.join(' · ');
  }

  function expiryText(batches) {
    const next = batches.find((batch) => batch.usedNext);
    if (!next || next.neverExpires || !next.expiresAt) return '';
    const verb = Number(next.remaining) === 1 ? 'expires' : 'expire';
    return `${plural(next.remaining, 'credit')} ${verb} ${formatDate(next.expiresAt)}. These get used first.`;
  }

  // Worded so it's true even right after a mid-month plan switch.
  function planText(plan) {
    if (!plan) return 'No monthly plan.';
    const base = `Plan: ${formatCount(plan.creditsPerMonth)} credits per month.`;
    const date = formatDate(plan.renewsAt);
    return date ? `${base} Your next batch arrives ${date}.` : base;
  }

  function batchText(batch) {
    const when = batch.neverExpires ? 'never expires' : `expires ${formatDate(batch.expiresAt)}`;
    const next = batch.usedNext ? ' (used next)' : '';
    return `${batch.label}: ${plural(batch.remaining, 'credit')}, ${when}${next}`;
  }

  function usageText(use) {
    const where = SURFACE_LABELS[use.surface] || 'Background removal';
    return `${formatDate(use.usedAt)} · ${where} · ${use.label}`;
  }

  // The trial banner is only for customers who have no plan and haven't
  // used the trial. Older servers don't send trialUsed, so fall back to
  // "no credits and no history".
  function shouldOfferTrial(account, plan, batches, usage) {
    if (plan) return false;
    if (typeof account.trialUsed === 'boolean') return !account.trialUsed;
    return batches.length === 0 && usage.length === 0;
  }

  // ---------------------------------------------------------------
  // API client
  // ---------------------------------------------------------------
  class ApiError extends Error {
    constructor(status, code, message) {
      super(message || code);
      this.name = 'ApiError';
      this.status = status;
      this.code = code;
    }
  }

  // 401 has no details.code, so check the status number first.
  function errorCode(status, data) {
    if (status === 401) return 'not_authorized';
    if (data && data.details && data.details.code) return data.details.code;
    if (data && data.error === 'no_credits') return 'no_credits';
    return 'unknown';
  }

    function createApi(apiBase, key, returnTo = '') {
    async function request(method, path, body) {
      const headers = { Authorization: `Bearer ${key}` };
      if (body !== undefined) headers['Content-Type'] = 'application/json';

      let response;
      try {
        response = await fetch(apiBase + path, {
          method,
          headers,
          cache: 'no-store',
          body: body === undefined ? undefined : JSON.stringify(body),
        });
      } catch (networkError) {
        throw new ApiError(0, 'network', networkError.message);
      }

      let data = null;
      try {
        data = await response.json();
      } catch {
        // Reply wasn't JSON. Handled below.
      }

      if (!response.ok) {
        throw new ApiError(response.status, errorCode(response.status, data), data && data.error);
      }
      if (!data) throw new ApiError(response.status, 'unknown', 'Empty reply');
      return data;
    }

    return {
      getAccount: () => request('GET', `/account?limit=${USAGE_LIMIT}`),
      createCheckout: (kind, priceId, email) => {
        const body = { kind };
        if (priceId) body.priceId = priceId;
                if (email) body.email = email; // only pre-fills Stripe's form
        if (returnTo) body.returnTo = returnTo; // Photo Studio credits page only
        return request('POST', '/checkout/session', body);
      },
            createPortal: () => request('POST', '/checkout/portal', returnTo ? { returnTo } : undefined),
    };
  }

  // Only ever send the browser to a real https address.
  function goTo(url) {
    let parsed;
    try {
      parsed = new URL(url);
    } catch {
      throw new ApiError(0, 'bad_redirect', 'Not a valid URL');
    }
    if (parsed.protocol !== 'https:') throw new ApiError(0, 'bad_redirect', 'Not https');
    window.location.assign(parsed.href);
  }

  // Remember the credit total before going to Stripe, so on return we can
  // tell if new credits already arrived. sessionStorage lasts for this tab only.
  function rememberTotal(total) {
    try {
      sessionStorage.setItem(TOTAL_BEFORE_CHECKOUT_KEY, String(total));
    } catch {
      // Storage blocked (private mode on some browsers): we fall back to polling.
    }
  }

  // Returns the remembered total (or null) and clears it so it's used only once.
  function takeRememberedTotal() {
    try {
      const raw = sessionStorage.getItem(TOTAL_BEFORE_CHECKOUT_KEY);
      sessionStorage.removeItem(TOTAL_BEFORE_CHECKOUT_KEY);
      const total = raw === null ? NaN : Number(raw);
      return Number.isFinite(total) ? total : null;
    } catch {
      return null;
    }
  }

  // Reads ?checkout=success|cancelled&kind=... then removes them from
  // the address bar so a page refresh doesn't show the message again.
  function readCheckoutReturn() {
    const url = new URL(window.location.href);
    const result = url.searchParams.get('checkout');
    if (result !== 'success' && result !== 'cancelled') return null;
    const kind = url.searchParams.get('kind');
    url.searchParams.delete('checkout');
    url.searchParams.delete('kind');
    try {
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch {
      // Not important if this fails.
    }
    return { result, kind };
  }

  // ---------------------------------------------------------------
  // View: everything that touches the page
  // ---------------------------------------------------------------
  function createView(root) {
    const find = (name) => root.querySelector(`[data-bgr-${name}]`);
    const nodes = {
      status: find('status'),
      total: find('total'),
      breakdown: find('breakdown'),
      expiry: find('expiry'),
      plan: find('plan'),
      paymentWarning: find('payment-warning'),
      batches: find('batches'),
      usage: find('usage'),
      trial: find('trial'),
      manageBilling: find('manage-billing'),
    };

    function setText(node, text) {
      if (!node) return;
      node.textContent = text || '';
      node.hidden = !text;
    }

    function renderList(node, lines, emptyText) {
      if (!node) return;
      const items = (lines.length ? lines : [emptyText]).map((line) => el('li', null, line));
      node.replaceChildren(...items);
    }

    // Shows or hides a list together with its fold-out heading.
    // The heading is the <details> box that wraps the list. If there is no
    // <details> box, only the list itself is hidden.
    function setListVisible(node, visible) {
      if (!node) return;
      const section = node.closest('details');
      const target = section && root.contains(section) ? section : node;
      target.hidden = !visible;
    }

    // Labels the monthly plan button for the credits picked in its dropdown:
    // "Subscribe" (no plan), "Change plan", or "Your plan · Manage".
    function renderPlanButtons(plan) {
      root.querySelectorAll('[data-bgr-kind="subscription"]').forEach((button) => {
        const card = button.closest('[data-bgr-card]');
        const isCurrent =
          Boolean(plan) && card && Number(card.dataset.bgrCredits) === Number(plan.creditsPerMonth);
        if (card) card.classList.toggle('is-current', isCurrent);
        if (isUnavailable(button)) return; // stays "Coming soon"

        let label = 'Subscribe';
        if (isCurrent) label = 'Your plan · Manage';
        else if (plan) label = 'Change plan';
        button.dataset.bgrLabel = label;
        if (!button.disabled || root.getAttribute('aria-busy') === 'false') button.textContent = label;
      });
    }

    return {
      showStatus(message, tone = 'info', link = null) {
        const node = nodes.status;
        if (!node) return;
        node.replaceChildren(document.createTextNode(message));
        if (link) {
          const anchor = el('a', null, link.text);
          anchor.href = link.href;
          node.append(' ', anchor);
        }
        node.className = `bgr-status bgr-status--${tone}`;
        node.hidden = false;
      },

      showAccountError() {
        setText(nodes.total, '–');
        setText(nodes.breakdown, "We couldn't load your credits.");
      },

      clearStatus() {
        if (!nodes.status) return;
        nodes.status.replaceChildren();
        nodes.status.hidden = true;
      },

      hideTrial() {
        if (nodes.trial) nodes.trial.hidden = true;
      },

      refreshPlanButtons: renderPlanButtons,

      renderAccount(account) {
        const balance = account.balance || {};
        const total = Number(balance.total) || 0;
        const batches = Array.isArray(account.batches) ? account.batches : [];
        const usage = Array.isArray(account.recentUsage) ? account.recentUsage : [];
        const plan = account.plan || null;
        const offerTrial = shouldOfferTrial(account, plan, batches, usage);

        setText(nodes.total, formatCount(total));
        if (total > 0) {
          setText(nodes.breakdown, breakdownText(balance));
        } else {
          setText(nodes.breakdown, offerTrial ? NO_CREDITS_WITH_TRIAL : NO_CREDITS);
        }
        setText(nodes.expiry, expiryText(batches));
        setText(nodes.plan, plan || total > 0 ? planText(plan) : '');

        if (nodes.paymentWarning) {
          nodes.paymentWarning.hidden = !(plan && PAYMENT_PROBLEM_STATUSES.includes(plan.status));
        }

        renderList(nodes.batches, batches.map(batchText), 'No credits right now.');
        renderList(nodes.usage, usage.map(usageText), 'No images yet.');
        setListVisible(nodes.batches, batches.length > 0);
        setListVisible(nodes.usage, usage.length > 0);
        renderPlanButtons(plan);

        if (nodes.trial) nodes.trial.hidden = !offerTrial;

        // Only show "Manage billing" when there's a billing account to open.
        // If the server doesn't say (older server), keep showing it.
        if (nodes.manageBilling) {
          nodes.manageBilling.hidden = account.hasBillingAccount === false;
        }
      },

      setBusy(busy, activeButton = null) {
        root.querySelectorAll('[data-bgr-action]').forEach((button) => {
          button.disabled = busy || isUnavailable(button);
          if (!busy && !isUnavailable(button) && button.dataset.bgrLabel) {
            button.textContent = button.dataset.bgrLabel;
          }
        });
        if (busy && activeButton) activeButton.textContent = 'Opening…';
        root.setAttribute('aria-busy', String(busy));
      },
    };
  }

  // ---------------------------------------------------------------
  // Controller: wires the API and the view together
  // ---------------------------------------------------------------
  async function init(root) {
    if (root.dataset.bgrReady === 'true') return;
    root.dataset.bgrReady = 'true';

    let config;
    try {
      config = readConfig(root);
    } catch (error) {
      // Logged out or not set up: Liquid already shows the right message.
      if (root.querySelector('[data-bgr-config]')) {
        console.error('[bg-removal] Could not read page settings:', error);
      }
      return;
    }
    if (!config.key) return;

        const api = createApi(config.apiBase, config.key, config.returnTo);
    const view = createView(root);
    const state = { account: null, busy: false };

    function showError(error) {
      if (!(error instanceof ApiError)) console.error('[bg-removal]', error);
      const code = error instanceof ApiError ? error.code : 'unknown';
      const link = code === 'not_authorized' ? { href: config.loginUrl, text: 'Log in' } : null;
      view.showStatus(ERROR_MESSAGES[code] || ERROR_MESSAGES.unknown, 'error', link);
      return code;
    }

    // Clicking can't fix these, so keep the buttons switched off.
    const isBlocking = (code) => code === 'not_authorized' || code === 'revoked';

    async function loadAccount({ quiet = false } = {}) {
      try {
        const account = await api.getAccount();
        state.account = account;
        view.renderAccount(account);
        if (!state.busy) view.setBusy(false);
        root.bgrAccount = account;
        root.dispatchEvent(new CustomEvent('bgr:account', { detail: account }));
        return account;
      } catch (error) {
        if (quiet) {
          console.warn('[bg-removal] Background refresh failed:', error);
          return null;
        }
        const code = showError(error);
        view.showAccountError();
        view.setBusy(isBlocking(code));
        return null;
      }
    }

    async function handleAction(button) {
      if (state.busy || isUnavailable(button)) return;
      const { bgrAction: action, bgrKind: kind, bgrPrice: priceId } = button.dataset;
      // Subscribers change plans on Stripe's billing page, never a second checkout.
      const usePortal =
        action === 'portal' || (kind === 'subscription' && Boolean(state.account && state.account.plan));

      state.busy = true;
      view.setBusy(true, button);
      try {
        const { url } = usePortal
          ? await api.createPortal()
          : await api.createCheckout(kind, priceId, config.email);
        if (!usePortal && state.account && state.account.balance) {
          rememberTotal(state.account.balance.total);
        }
        goTo(url); // Buttons stay off: the browser is leaving the page.
      } catch (error) {
        state.busy = false;
        const code = showError(error);
        view.setBusy(isBlocking(code));
        if (code === 'trial_already_used') view.hideTrial();
        if (code === 'already_subscribed') loadAccount({ quiet: true });
      }
    }

    // Credits arrive by Stripe webhook, usually within seconds.
    async function waitForCredits(startTotal) {
      for (const delay of POLL_DELAYS_MS) {
        await sleep(delay);
        const account = await loadAccount({ quiet: true });
        if (account && account.balance && account.balance.total !== startTotal) {
          view.showStatus('Your new credits have arrived!', 'success');
          return;
        }
      }
      view.showStatus(
        "Your purchase went through. If your new credits aren't showing yet, refresh this page in a minute.",
        'info'
      );
    }

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-bgr-action]');
      if (!button || !root.contains(button) || button.disabled) return;
      event.preventDefault();
      handleAction(button);
    });

    // The removal tool tells us when it spent a credit, so refresh the numbers.
    root.addEventListener('bgr:credits-changed', () => loadAccount({ quiet: true }));

    // A pricing dropdown changed: relabel the monthly button for that choice.
    root.addEventListener('bgr:pricing-change', () => {
      if (state.account) view.refreshPlanButtons(state.account.plan || null);
    });

    // If the customer presses the browser's Back button on Stripe, the browser
    // shows its saved copy of this page. Clear old messages, turn the buttons
    // back on, and refresh the credit numbers.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      state.busy = false;
      takeRememberedTotal(); // they didn't buy anything, so forget the saved total
      view.clearStatus();
      view.setBusy(false);
      loadAccount({ quiet: true });
    });

    const checkoutReturn = readCheckoutReturn();
    if (checkoutReturn) {
      if (checkoutReturn.result === 'success') {
        view.showStatus(SUCCESS_MESSAGES[checkoutReturn.kind] || DEFAULT_SUCCESS_MESSAGE, 'success');
      } else {
        view.showStatus(CANCELLED_MESSAGE, 'info');
      }
    }

    const totalBefore = takeRememberedTotal(); // always cleared, even on cancel
    const account = await loadAccount();
    if (account && checkoutReturn && checkoutReturn.result === 'success') {
      const totalNow = account.balance ? account.balance.total : 0;
      if (totalBefore !== null && totalNow !== totalBefore) {
        view.showStatus('Your new credits have arrived!', 'success');
      } else {
        waitForCredits(totalBefore !== null ? totalBefore : totalNow);
      }
    }
  }

  // ---------------------------------------------------------------
  // Start
  // ---------------------------------------------------------------
  function boot(scope = document) {
    scope.querySelectorAll('[data-bgr-root]').forEach((root) => {
      init(root).catch((error) => console.error('[bg-removal] Startup failed:', error));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot());
  } else {
    boot();
  }
})();
